import type { RouteManifestEntry } from "@/lib/seo/route-manifest";

export interface WebMCPPageEntry {
  path: string;
  markdownPath: string;
}

function localePrefix(locale: string): string {
  return locale === "en" ? "" : `/${locale.replace(/^en-/, "").toLowerCase()}`;
}

const LOCALE_HOME_PATHS = new Set([
  "/en",
  "/en-GB",
  "/en-IE",
  "/en-AU",
  "/gb",
  "/ie",
  "/au",
  "/de",
  "/es",
  "/nl",
]);

function homepageMarkdownPath(localizedPath: string): string {
  return localizedPath === "/"
    ? "/index.md"
    : `${localizedPath.replace(/\/$/, "")}/index.md`;
}

export function buildLocalizedPageList(
  manifest: RouteManifestEntry[],
  locale: string
): WebMCPPageEntry[] {
  const prefix = localePrefix(locale);

  return manifest
    .filter(({ locales }) => locales.includes(locale))
    .map(({ path }) => {
      const suffix = path ? `/${path}` : "";
      const localizedPath = `${prefix}${suffix}` || "/";
      return {
        path: localizedPath,
        markdownPath:
          path === ""
            ? homepageMarkdownPath(localizedPath)
            : `${localizedPath}.md`,
      };
    });
}

export function markdownCandidatesForPath(input: string): [string, string] {
  if (
    !input.startsWith("/") ||
    input.startsWith("//") ||
    input.includes("://")
  ) {
    throw new Error("Expected a same-site path beginning with /");
  }
  if (input.includes("\\")) {
    throw new Error("Expected a same-site path without backslashes");
  }

  const path = input.split(/[?#]/, 1)[0]!.replace(/\/+$/, "") || "/";
  if (path.includes("..")) {
    throw new Error("Expected a same-site path without traversal");
  }

  const withoutLeadingSlash = path.replace(/^\//, "");
  if (path === "/" || LOCALE_HOME_PATHS.has(path)) {
    const literal = homepageMarkdownPath(path);
    return [
      literal,
      `/_markdown/${literal.replace(/^\//, "").replace(/\.md$/, "")}`,
    ];
  }

  return [`${path}.md`, `/_markdown/${withoutLeadingSlash}`];
}
